/*
  menara-catalogue.js — filtrage, tri et « Voir plus » sans rechargement.

  Chaque commande reste un vrai lien ou un vrai formulaire Shopify : sans
  JavaScript, la page se recharge et tout fonctionne. Avec JavaScript, la
  section est redemandée à Shopify (Section Rendering API) pour la même
  adresse, puis remplacée sur place.
*/

class MenaraCatalogue extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.base = this.dataset.base || '';
    this.annonce = this.querySelector('[data-cat-annonce]');

    this.surClic = (e) => this.clic(e);
    this.surChange = (e) => this.change(e);
    this.surEnvoi = (e) => this.envoi(e);
    this.surHistorique = (e) => this.historique(e);

    this.addEventListener('click', this.surClic);
    this.addEventListener('change', this.surChange);
    this.addEventListener('submit', this.surEnvoi);
    window.addEventListener('popstate', this.surHistorique);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.surClic);
    this.removeEventListener('change', this.surChange);
    this.removeEventListener('submit', this.surEnvoi);
    window.removeEventListener('popstate', this.surHistorique);
    if (this.controle) this.controle.abort();
  }

  urlSection(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.toString();
  }

  async recuperer(url) {
    if (this.controle) this.controle.abort();
    this.controle = new AbortController();
    const reponse = await fetch(this.urlSection(url), { signal: this.controle.signal, credentials: 'same-origin' });
    if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
    return new DOMParser().parseFromString(await reponse.text(), 'text/html');
  }

  clic(e) {
    const lien = e.target.closest('a[data-cat-lien], a[data-cat-plus]');
    if (!lien || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (lien.hasAttribute('data-cat-plus')) {
      this.plus(lien);
    } else {
      this.charger(lien.href, { empiler: true, focus: lien.getAttribute('href') });
    }
  }

  change(e) {
    const select = e.target.closest('[data-cat-select]');
    if (!select || !select.form) return;
    const url = new URL(select.form.action, window.location.origin);
    url.searchParams.set('sort_by', select.value);
    this.charger(url.toString(), { empiler: true, focusTri: true });
  }

  envoi(e) {
    if (!e.target.matches('[data-cat-tri]')) return;
    e.preventDefault();
    const select = e.target.querySelector('[data-cat-select]');
    if (select) this.change({ target: select });
  }

  historique() {
    if (this.base && !window.location.pathname.startsWith(this.base)) return;
    this.charger(window.location.href, { empiler: false });
  }

  async charger(url, options = {}) {
    const zone = this.querySelector('[data-cat-zone]');
    if (!zone) return;
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.recuperer(url);
      const neuve = doc.querySelector('[data-cat-zone]');
      if (!neuve) {
        window.location.href = url;
        return;
      }
      zone.innerHTML = neuve.innerHTML;
      if (options.empiler) history.pushState({ menaraCatalogue: true }, '', url);

      // Le focus reste sur la commande utilisée, retrouvée dans le nouveau contenu.
      if (options.focus) {
        const cible = Array.from(zone.querySelectorAll('a[data-cat-lien]')).find((a) => a.getAttribute('href') === options.focus);
        const repli = zone.querySelector('.cat__filtre.est-actif, .cat__reinit');
        (cible || repli || zone).focus({ preventScroll: true });
      }
      if (options.focusTri) {
        const select = zone.querySelector('[data-cat-select]');
        if (select) select.focus({ preventScroll: true });
      }

      if (this.annonce) {
        const compte = zone.querySelector('[data-cat-compte]');
        const vide = zone.querySelector('.cat__vide p');
        this.annonce.textContent = (compte || vide) ? (compte || vide).textContent.trim() : '';
      }
    } catch (erreur) {
      if (erreur.name !== 'AbortError') window.location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async plus(lien) {
    const grille = this.querySelector('[data-cat-grille]');
    if (!grille) return;
    lien.classList.add('est-charge');
    lien.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.recuperer(lien.href);
      const nouveaux = Array.from(doc.querySelectorAll('[data-cat-grille] > [data-cat-item]'));
      const avant = grille.children.length;
      nouveaux.forEach((li) => grille.appendChild(document.importNode(li, true)));

      const suite = this.querySelector('[data-cat-suite]');
      const suiteNeuve = doc.querySelector('[data-cat-suite]');
      if (suite) {
        const garder = suiteNeuve && suiteNeuve.querySelector('[data-cat-plus]');
        if (garder) {
          suite.replaceWith(document.importNode(suiteNeuve, true));
          // Le lien vers la page précédente n'a pas de sens quand tout est affiché à la suite.
          const precedent = this.querySelector('[data-cat-suite] .cat__precedent');
          if (precedent) precedent.remove();
        } else {
          suite.remove();
        }
      }

      const premier = grille.children[avant];
      const lienPremier = premier && premier.querySelector('.cat__titre a');
      if (lienPremier) lienPremier.focus({ preventScroll: true });
      if (this.annonce) this.annonce.textContent = `${nouveaux.length} ${this.dataset.libelleAjoutes || 'tableaux ajoutés'}`;
    } catch (erreur) {
      if (erreur.name !== 'AbortError') window.location.href = lien.href;
    }
  }
}

if (!customElements.get('menara-catalogue')) {
  customElements.define('menara-catalogue', MenaraCatalogue);
}
